import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ServiceUri } from "@common/configuration/service-uri";
import { Logger } from "@common/lib/logger/logger";
import { RouteService } from "@common/route/route.service";
import { ShellStyleConstants } from "@common/shell/shell-style.constants";
import { BaseComponentService } from "@common/ux/base.component/base.component.service";
import { ResponseOutputItemDoneEvent } from "openai/resources/responses/responses";
import { lastValueFrom } from "rxjs";
import { CHAT_AI_ORG_PROVIDER, IChatAiOrgProvider } from "./chat-ai-org-provider.interface";

enum ClientToolCallName {
    NavigateToPath = "NavigateToPath",
    GetCurrentView = "GetCurrentView",
}

interface IClientToolCallCompletionBindingModel {
    organisationId: number;
    functionName: string;
    responseId: string;
    result: string;
}

const logger = Logger.getLogger("Client tool-call handler service");

// Keep attributes that materially affect the meaning or behaviour of the rendered DOM once styling and framework metadata are removed.
const PreservedAttributeNames = ["alt", "checked", "colspan", "disabled", "href", "name", "placeholder", "readonly", "role", "rowspan", "selected", "src", "title", "type", "value"];
const NonRemovableEmptyElementTagNames = ["br", "hr", "img", "input", "option", "select", "textarea", "visual-summary"];

@Injectable({
    providedIn: "root",
})
export class ClientToolCallHandlerService {
    private readonly NotifyClientToolCallCompletionPath = "/NotifyClientToolCallCompletion";

    public constructor(
        @Inject(CHAT_AI_ORG_PROVIDER) private orgService: IChatAiOrgProvider,
        private httpClient: HttpClient,
        private routeService: RouteService,
        private baseComponentService: BaseComponentService,
    ) { }

    public async onOutputItemDone(event: ResponseOutputItemDoneEvent) {
        if (event.item.type === "function_call" && event.item.id) {
            // only handle functionName from ClientToolCallName enum
            if (Object.values(ClientToolCallName).includes(event.item.name as ClientToolCallName)) {
                let result = "function call executed";
                try {
                    const argsObject = JSON.parse(event.item.arguments || "{}");
                    logger.info(`Function Call: ${event.item.name}`, argsObject);

                    switch (event.item.name) {
                        case ClientToolCallName.NavigateToPath:
                            await this.navigateToPath(argsObject);
                            result = "Navigation completed";
                            break;
                        case ClientToolCallName.GetCurrentView:
                            this.baseComponentService.triggerElementInView();
                            await new Promise((resolve) => setTimeout(resolve, 2000)); // wait 2 seconds to ensure the triggered element is rendered before capturing
                            const mainView = document.querySelector(ShellStyleConstants.MainViewClassSelect);
                            result = `The current view content in html form is: ${this.getCleanInnerHTML(mainView as HTMLElement)}`;
                            break;
                        default:
                            result = "Function not implemented";
                            break;
                    }
                } catch (e: any) {
                    logger.error("Function call failed:", e);
                    result = `Function call failed: ${e.message}`;
                } finally {
                    if (result) {
                        await lastValueFrom(this.postCompletion(event.item.name, event.item.id, result));
                    }
                }
            }
        }
    }

    private postCompletion(functionName: string, responseId: string, result: string) {
        const url = `${ServiceUri.AIChatServiceUri}${this.NotifyClientToolCallCompletionPath}`;
        const bindingModel: IClientToolCallCompletionBindingModel = {
            organisationId: this.orgService.getOrganisationId(),
            functionName,
            responseId,
            result,
        };
        return this.httpClient.post(url, bindingModel);
    }

    private getCleanInnerHTML(element?: HTMLElement): string {
        if (!element) {
            return "";
        }

        const clone = element.cloneNode(true) as HTMLElement;
        const nodeIterator = document.createNodeIterator(clone, NodeFilter.SHOW_COMMENT);
        const comments: Node[] = [];
        let currentNode = nodeIterator.nextNode();
        while (currentNode) {
            comments.push(currentNode);
            currentNode = nodeIterator.nextNode();
        }
        comments.forEach((node) => node.parentNode?.removeChild(node));

        this.removeNonVisualElements(clone);
        this.summariseGeneratedVisuals(clone);
        this.stripUnhelpfulAttributes(clone);
        this.removeEmptyElements(clone);

        return clone.innerHTML;
    }

    private removeNonVisualElements(root: HTMLElement): void {
        root.querySelectorAll("script, style, noscript, template, router-outlet, dx-load-panel").forEach((element) => element.remove());
    }

    private summariseGeneratedVisuals(root: HTMLElement): void {
        root.querySelectorAll("dx-chart svg, dx-pie-chart svg, dx-linear-gauge svg").forEach((element) => {
            const svg = element as SVGElement;
            const summary = this.getSvgTextSummary(svg);
            if (!summary) {
                svg.remove();
                return;
            }

            const summaryElement = root.ownerDocument.createElement("visual-summary");
            summaryElement.textContent = summary;
            svg.replaceWith(summaryElement);
        });
    }

    private getSvgTextSummary(svg: SVGElement): string {
        const values: string[] = [];

        svg.querySelectorAll("text").forEach((textElement) => {
            const tspans = Array.from(textElement.querySelectorAll("tspan"));
            const value = tspans.length > 0
                ? tspans.map((tspan) => this.normaliseText(tspan.textContent)).filter(Boolean).join(" ")
                : this.normaliseText(textElement.textContent);

            if (value) {
                values.push(value);
            }
        });

        const hostTagName = svg.parentElement?.tagName.toLowerCase();
        if (hostTagName === "dx-linear-gauge") {
            return values.at(-1) ?? "";
        }

        const uniqueValues = values.filter((value, index) => values.indexOf(value) === index);
        return uniqueValues.join(" | ");
    }

    private stripUnhelpfulAttributes(root: HTMLElement): void {
        const allElements = [root, ...Array.from(root.querySelectorAll("*"))];
        allElements.forEach((element) => {
            const attributesToRemove: string[] = [];
            for (let i = 0; i < element.attributes.length; i++) {
                const attrName = element.attributes[i].name;
                if (!this.shouldKeepAttribute(attrName)) {
                    attributesToRemove.push(attrName);
                }
            }

            attributesToRemove.forEach((attribute) => element.removeAttribute(attribute));
        });
    }

    private shouldKeepAttribute(attributeName: string): boolean {
        if (attributeName.startsWith("aria-")) {
            return true;
        }

        return PreservedAttributeNames.includes(attributeName);
    }

    private removeEmptyElements(root: HTMLElement): void {
        Array.from(root.querySelectorAll("*")).reverse().forEach((element) => {
            if (this.canRemoveEmptyElement(element)) {
                element.remove();
            }
        });
    }

    private canRemoveEmptyElement(element: Element): boolean {
        const tagName = element.tagName.toLowerCase();
        if (NonRemovableEmptyElementTagNames.includes(tagName)) {
            return false;
        }

        if (element.childElementCount > 0) {
            return false;
        }

        if (this.normaliseText(element.textContent)) {
            return false;
        }

        return !Array.from(element.attributes).some((attribute) => this.shouldKeepAttribute(attribute.name));
    }

    private normaliseText(value?: string | null): string {
        return value?.replace(/\s+/g, " ").trim() ?? "";
    }

    private async navigateToPath(args: any) {
        if (args.navigationPath) {
            let navigationPath: string = args.navigationPath;
            // iterate through args except navigationPath and replace {key} with the value from args
            Object.keys(args).forEach((key) => {
                if (key !== "navigationPath") {
                    const placeholder = `{${key}}`;
                    navigationPath = navigationPath.replace(placeholder, args[key]);
                }
            });

            logger.debug("Current url: " + this.routeService.currentUrl + " vs target: " + navigationPath);
            if (this.routeService.currentUrl.includes(navigationPath)) {
                logger.debug("Already at target path, no navigation needed");
                throw new Error("Already at target path. No navigation needed.");
            }

            await this.routeService.navigateByUrl(navigationPath);
            // delay after navigate to allow for redirection if no permission (otherwise, it will be treated as success even if it failed)
            await new Promise((resolve) => setTimeout(resolve, 1000));
            if (!this.routeService.currentUrl.includes(navigationPath)) {
                logger.error("Path after navigation not matching target: ", this.routeService.currentUrl);
                throw new Error("Navigation failed");
            }
        } else {
            throw new Error(`navigationPath is required for NavigateToPath`);
        }
    }
}
